import { Button } from "@/components/ui/button";
import { FictionalVehicle, fmt } from "@/hooks/useVehicleSim";
import { ArrowLeft, ArrowRight, AlertTriangle } from "lucide-react";

interface Props {
  vehicle: FictionalVehicle;
  effectivePrice: number;
  downPayment: number;
  hiddenCosts: { salesTax: number; registration: number; dealerFee: number; titleFee: number; total: number };
  upfrontCash: number;
  monthlyPayment: number;
  totalMonthlyCost: number;
  firstYearCost: number;
  onContinue: () => void;
  onBack: () => void;
}

export default function HiddenCostsScreen({ vehicle, effectivePrice, downPayment, hiddenCosts, upfrontCash, monthlyPayment, totalMonthlyCost, firstYearCost, onContinue, onBack }: Props) {
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="font-heading text-lg font-bold text-foreground">Hidden Costs Revealed</h2>
        <p className="text-sm text-muted-foreground">The purchase price is only part of the picture.</p>
      </div>

      <div className="glass-card glow-border p-5 space-y-4">
        <div className="flex items-center gap-2 text-amber-600 bg-amber-500/10 p-3 rounded-xl">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <p className="text-xs font-semibold">Sticker Price ≠ Actual Cost</p>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-heading font-semibold text-muted-foreground uppercase tracking-wider">Upfront Costs</p>
          <div className="space-y-1.5">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Vehicle Price</span><span className="text-foreground">{fmt(effectivePrice)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Down Payment</span><span className="text-foreground">−{fmt(downPayment)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Sales Tax (7%)</span><span className="text-foreground">{fmt(hiddenCosts.salesTax)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Registration</span><span className="text-foreground">{fmt(hiddenCosts.registration)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Dealer/Processing Fee</span><span className="text-foreground">{fmt(hiddenCosts.dealerFee)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Title Fee</span><span className="text-foreground">{fmt(hiddenCosts.titleFee)}</span></div>
            <div className="border-t border-border pt-1.5 flex justify-between text-sm font-semibold">
              <span className="text-foreground">Total Cash Needed</span>
              <span className="text-primary">{fmt(upfrontCash)}</span>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-heading font-semibold text-muted-foreground uppercase tracking-wider">Monthly Ownership Costs</p>
          <div className="space-y-1.5">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Loan Payment</span><span className="text-foreground">{fmt(monthlyPayment)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Insurance</span><span className="text-foreground">{fmt(vehicle.insurance_monthly)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Maintenance</span><span className="text-foreground">{fmt(vehicle.maintenance_monthly)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Fuel / Energy</span><span className="text-foreground">{fmt(vehicle.fuel_monthly)}</span></div>
            <div className="border-t border-border pt-1.5 flex justify-between text-sm font-semibold">
              <span className="text-foreground">Total Monthly Cost</span>
              <span className="text-primary">{fmt(totalMonthlyCost)}</span>
            </div>
          </div>
        </div>

        <div className="glass-card p-3 text-center">
          <p className="text-xs text-muted-foreground">Estimated First-Year Ownership Cost</p>
          <p className="text-xl font-heading font-bold text-foreground">{fmt(firstYearCost)}</p>
        </div>
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="gap-2"><ArrowLeft className="w-4 h-4" /> Back</Button>
        <Button onClick={onContinue} className="flex-1 gap-2">Continue <ArrowRight className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
